<template>
  <div v-if="props.show" class="fixed z-10 inset-0 bg-gray-900 bg-opacity-60 overflow-y-auto flex items-center justify-center text-base">
    <div class="bg-white rounded-lg shadow-xl w-4/12 h-[40vh]">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b">
        <h4 class="text-lg text-gray-800 font-bold">Upload Company Beneficiaries</h4>
        <button
          class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-custom-yellow"
          type="button"
          @click="emitClose"
          aria-label="Close"
        >
          <span class="sr-only">Dismiss</span>
          <svg
            class="w-3 h-3"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 14 14"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
        </button>
      </div>

      <!-- Body -->
      <div class="flex justify-center mt-6">
        <div class="w-full max-w-md flex flex-col space-y-4">
          <!-- File Input -->
          <div>
            <label class="block text-sm font-medium text-gray-700">
              <span class="text-red-500">*</span>
                Upload File
              <button
                class="text-sm text-green-700 cursor-pointer"
                @click.stop="downloadTemplate"
                >(Download Excel Template)
              </button>
            </label>
            <input
              type="file"
              accept=".csv,.xlsx"
              @change="handleFileChange"
              class="w-full px-3 py-2 mt-3 text-sm bg-gray-100 border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
            />
            <p v-if="error" class="text-red-500 text-sm mt-1">{{ error }}</p>
          </div>
          <!-- Upload Button -->
          <div>
            <CustomButton
              v-if="!isLoading"
              class="flex justify-center w-full text-center"
              label="Upload File"
              @click.prevent="uploadFile"
            />
            <CustomButton
              v-else
              class="flex justify-center w-full text-center"
              label="Uploading..."
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue'
import CustomButton from "@/components/CustomButton.vue"
import { useToast } from "vue-toast-notification"
import apiClient from "@/services/api"

const props = defineProps({
  show: { type: Boolean, default: false, required: true },
  companyId: { type: String, required: true }
})

const emit = defineEmits(['close', 'upload-success'])

const toast = useToast()
const isLoading = ref(false)
const file = ref(null)
const error = ref(null)

// Download Upload Sheet Template
const downloadTemplate = async () => {
  try {
    const response = await apiClient.downloadCompanyBeneficiariesTemplate();

    if (!response.ok) {
      throw new Error(`Failed to download template: ${response.status} ${response.statusText}`)
    }

    let filename = "company_beneficiaries.xlsx"
    const disposition = response.headers.get("Content-Disposition");

    if (disposition) {
      const match = disposition.match(/filename="([^"]+)"/);
      if (match?.[1]) filename = match[1];
    }

    const blob = await response.blob();
    const typeBlob = new Blob([blob], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })

    const url = window.URL.createObjectURL(typeBlob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

  } catch (error) {
    toast.error(error.message || 'Failed to download template.')
    console.log('failed to download template', error)
  }
}

// Handle file selection
const handleFileChange = (event) => {
  const selectedFile = event.target.files?.[0] || null

  if (selectedFile) {
    const validTypes = [
      "text/csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ]

    if (!validTypes.includes(selectedFile.type)) {
      error.value = "Please select a .csv or .xlsx file."
      file.value = null
      return;
    } else {
      file.value = selectedFile
      error.value = null
    }
  } else {
    file.value = null
  }
}

const emitClose = () => {
  file.value = null
  error.value = null
  emit('close');
}

const emitUploadSuccess = () => {
  emitClose();
  emit('upload-success');
}

const uploadFile = async () => {
  if (!file.value) {
    error.value = "No file selected."
    return;
  }

  isLoading.value = true;
  try {
    const formData = new FormData()
    formData.append('file', file.value)
    formData.append('company_id', props.companyId)

    const response = await apiClient.uploadCompanyBeneficiaries(formData)

    if (response.success) {
      toast.success(response.message)
      emitUploadSuccess();
    } else {
      toast.error(response.message || response.errors?.join(', ') || "Failed to upload file.")
    }
  } catch (error) {
    toast.error(error.message || "Failed to upload file.")
    console.log('failed to upload file', error)
  } finally {
    isLoading.value = false;
  }
}
</script>